import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  signInAnonymously,
  onAuthStateChanged,
  User as FirebaseUser
} from "firebase/auth";

import { auth } from "@/app/providers/Firebase";
import { writeAuthDebug } from "@/app/providers/authDebug";

const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: "select_account" });

function isMobileOrInAppBrowser(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const userAgent = window.navigator.userAgent ?? "";
  const isMobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    userAgent
  );
  const isCoarsePointer =
    window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
  const isInAppBrowser = /FBAN|FBAV|Instagram|Line|Twitter|GSA|Snapchat|DuckDuckGo|LinkedInApp/i.test(
    userAgent
  );

  return isMobileUa || isCoarsePointer || isInAppBrowser;
}

function isInAppBrowser(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const userAgent = window.navigator.userAgent ?? "";
  return /FBAN|FBAV|Instagram|Line|Twitter|GSA|Snapchat|DuckDuckGo|LinkedInApp/i.test(
    userAgent
  );
}

export const authService = {
  async consumeRedirectResult(): Promise<FirebaseUser | null> {
    writeAuthDebug("consume_redirect_result:start");
    const result = await getRedirectResult(auth);
    writeAuthDebug("consume_redirect_result:success", {
      firebaseUid: result?.user?.uid ?? null,
      firebaseEmail: result?.user?.email ?? null,
      detail: result?.user
        ? "Redirect returned a Firebase user."
        : "Redirect returned no user."
    });
    return result?.user ?? null;
  },

  async signInWithGoogle(): Promise<FirebaseUser | null> {
    if (isInAppBrowser()) {
      writeAuthDebug("sign_in:redirect_mobile", {
        detail: "Using redirect flow for in-app browser."
      });
      await signInWithRedirect(auth, provider);
      return null;
    }

    try {
      writeAuthDebug("sign_in:popup_start");
      const result = await signInWithPopup(auth, provider);
      writeAuthDebug("sign_in:popup_success", {
        firebaseUid: result.user.uid,
        firebaseEmail: result.user.email ?? null
      });
      return result.user;
    } catch (err) {
      const code = err?.code as string | undefined;
      writeAuthDebug("sign_in:popup_error", {
        errorCode: code,
        errorMessage: err?.message
      });
      const shouldFallbackToRedirect =
        code === "auth/popup-blocked" ||
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request" ||
        code === "auth/operation-not-supported-in-this-environment";

      if (!shouldFallbackToRedirect) {
        throw err;
      }

      writeAuthDebug("sign_in:redirect_fallback", {
        errorCode: code,
        errorMessage: err?.message,
        detail: isMobileOrInAppBrowser()
          ? "Popup failed on mobile; falling back to redirect."
          : "Popup failed on desktop; falling back to redirect."
      });
      await signInWithRedirect(auth, provider);
      return null;
    }
  },

  async signOut() {
    await signOut(auth);
  },

  async signInAsGuest(): Promise<FirebaseUser> {
    const result = await signInAnonymously(auth);
    return result.user;
  },

  subscribe(cb: (user: FirebaseUser | null) => void) {
    return onAuthStateChanged(auth, cb);
  }
};
